import type { Lang } from '../../types/funnel';
import type { LegalDocument } from '../../types/legal';

const PRIVACY_URL = 'https://floridalocalmovers.instamoversfl.com/privacy-policy';

/** Owner-provided Terms & Conditions text (published verbatim in English). Spanish is an equivalent translation. */
export const termsAndConditions: Record<Lang, LegalDocument> = {
  en: {
    title: 'Terms & Conditions',
    effective: 'Effective date: October 6, 2026',
    sections: [
      {
        heading: 'Website Operator',
        body: [
          'This website and the InstaMovers Florida SMS program are operated by InstaMovers Florida LLC. Florida Local Movers is a moving service website operated by InstaMovers Florida LLC.',
        ],
      },
      {
        heading: 'Website and Quote Requests',
        body: [
          'You may use this website to learn about moving services, request a quote and contact us. Please provide accurate information about your move and contact details.',
          'Submitting a form requests contact or an estimate. It does not by itself confirm a reservation, final price, payment or completed moving service agreement.',
        ],
      },
      {
        heading: 'Moving Service Agreements',
        body: [
          'Final service scope, pricing, availability, deposits, cancellation terms, payment obligations and applicable moving service terms are established in the estimate and service documents provided for your move. These website and SMS terms do not replace those documents.',
        ],
      },
      {
        heading: 'SMS Program Description',
        body: [
          'With your affirmative consent, InstaMovers Florida LLC may send text messages in the categories you select:',
          'Non-marketing messages: responses concerning your requested moving quote, booking confirmations, appointment coordination and moving service updates.',
          'Marketing messages: moving service offers, discounts and promotional follow-up.',
          'Messages may be sent using automated technology.',
        ],
      },
      {
        heading: 'Voluntary and Separate Consent',
        body: [
          'Marketing and non-marketing SMS consent are collected separately. Each SMS consent checkbox is optional and unchecked by default. You may select either category, both categories or neither.',
          'Providing your telephone number does not by itself constitute SMS consent. Consent is not a condition of purchase or of requesting a quote.',
          'You must be at least 18 years old and be the subscriber or authorized user of the telephone number you provide.',
        ],
      },
      {
        heading: 'Frequency and Charges',
        body: [
          'Message frequency varies according to your requests, services and selected communication preferences.',
          'Message and data rates may apply to messages sent and received. Contact your wireless provider for information about your plan and charges.',
        ],
      },
      {
        heading: 'Opting Out',
        body: [
          'You can cancel SMS communications at any time by replying STOP to a message from us. You may receive one confirmation of your unsubscribe request. After that, we will stop sending SMS messages unless you provide new affirmative consent.',
          'To enroll again, use the applicable consent form or contact us for instructions.',
        ],
      },
      {
        heading: 'Help and Customer Support',
        body: ['Reply HELP to a message for assistance, or contact:', { lines: ['Email: instamoversfl@gmail.com', 'Telephone: +1 (656) 268-0800'] }],
      },
      {
        heading: 'Delivery and Carrier Liability',
        body: ['SMS delivery is subject to wireless carrier and network availability. Carriers are not liable for delayed or undelivered messages.'],
      },
      {
        heading: 'Privacy',
        body: [
          'Our Privacy Policy explains how we handle personal information and protect mobile information and SMS consent.',
          { before: 'Read our Privacy Policy: ', linkText: PRIVACY_URL, to: '/privacy-policy' },
        ],
      },
      {
        heading: 'Updates',
        body: ['We may update these terms and post the revised effective date. Changes do not automatically enroll you in additional SMS categories or override an opt-out.'],
      },
      {
        heading: 'Contact',
        body: [{ lines: ['InstaMovers Florida LLC', 'Email: instamoversfl@gmail.com', 'Telephone: +1 (656) 268-0800', 'Website: https://floridalocalmovers.instamoversfl.com'] }],
      },
    ],
  },
  es: {
    title: 'Términos y Condiciones',
    effective: 'Fecha de entrada en vigor: 6 de octubre de 2026',
    sections: [
      {
        heading: 'Operador del sitio web',
        body: [
          'Este sitio web y el programa de SMS de InstaMovers Florida son operados por InstaMovers Florida LLC. Florida Local Movers es un sitio web de servicios de mudanza operado por InstaMovers Florida LLC.',
        ],
      },
      {
        heading: 'Sitio web y solicitudes de cotización',
        body: [
          'Puede usar este sitio web para informarse sobre servicios de mudanza, solicitar una cotización y contactarnos. Proporcione información precisa sobre su mudanza y sus datos de contacto.',
          'Enviar un formulario es una solicitud de contacto o de estimado. Por sí solo no confirma una reserva, un precio final, un pago ni un acuerdo de servicio de mudanza completado.',
        ],
      },
      {
        heading: 'Acuerdos de servicio de mudanza',
        body: [
          'El alcance final del servicio, los precios, la disponibilidad, los depósitos, los términos de cancelación, las obligaciones de pago y los términos aplicables del servicio de mudanza se establecen en el estimado y en los documentos de servicio proporcionados para su mudanza. Estos términos del sitio web y de SMS no reemplazan esos documentos.',
        ],
      },
      {
        heading: 'Descripción del programa de SMS',
        body: [
          'Con su consentimiento afirmativo, InstaMovers Florida LLC puede enviar mensajes de texto en las categorías que usted seleccione:',
          'Mensajes que no son de marketing: respuestas sobre su cotización de mudanza solicitada, confirmaciones de reserva, coordinación de citas y actualizaciones del servicio de mudanza.',
          'Mensajes de marketing: ofertas de servicios de mudanza, descuentos y seguimiento promocional.',
          'Los mensajes pueden enviarse mediante tecnología automatizada.',
        ],
      },
      {
        heading: 'Consentimiento voluntario y por separado',
        body: [
          'El consentimiento para SMS de marketing y el de SMS que no son de marketing se recopilan por separado. Cada casilla de consentimiento para SMS es opcional y no está marcada de forma predeterminada. Puede seleccionar cualquiera de las categorías, ambas o ninguna.',
          'Proporcionar su número de teléfono no constituye por sí solo consentimiento para SMS. El consentimiento no es una condición de compra ni para solicitar una cotización.',
          'Debe tener al menos 18 años y ser el titular o usuario autorizado del número de teléfono que proporcione.',
        ],
      },
      {
        heading: 'Frecuencia y cargos',
        body: [
          'La frecuencia de los mensajes varía según sus solicitudes, servicios y preferencias de comunicación seleccionadas.',
          'Pueden aplicar tarifas de mensajes y datos a los mensajes enviados y recibidos. Comuníquese con su proveedor de telefonía móvil para obtener información sobre su plan y sus cargos.',
        ],
      },
      {
        heading: 'Cancelación',
        body: [
          'Puede cancelar las comunicaciones por SMS en cualquier momento respondiendo STOP a un mensaje nuestro. Es posible que reciba una confirmación de su solicitud de cancelación. Después de eso, dejaremos de enviarle mensajes SMS a menos que otorgue un nuevo consentimiento afirmativo.',
          'Para inscribirse de nuevo, use el formulario de consentimiento correspondiente o contáctenos para recibir instrucciones.',
        ],
      },
      {
        heading: 'Ayuda y atención al cliente',
        body: ['Responda HELP a un mensaje para obtener ayuda, o comuníquese a:', { lines: ['Correo electrónico: instamoversfl@gmail.com', 'Teléfono: +1 (656) 268-0800'] }],
      },
      {
        heading: 'Entrega y responsabilidad de los operadores',
        body: ['La entrega de SMS está sujeta a la disponibilidad del operador de telefonía móvil y de la red. Los operadores no son responsables de los mensajes retrasados o no entregados.'],
      },
      {
        heading: 'Privacidad',
        body: [
          'Nuestra Política de Privacidad explica cómo manejamos la información personal y protegemos la información móvil y el consentimiento para SMS.',
          { before: 'Lea nuestra Política de Privacidad: ', linkText: PRIVACY_URL, to: '/privacy-policy' },
        ],
      },
      {
        heading: 'Actualizaciones',
        body: ['Podemos actualizar estos términos y publicar la fecha de entrada en vigor revisada. Los cambios no lo inscriben automáticamente en categorías adicionales de SMS ni anulan una cancelación.'],
      },
      {
        heading: 'Contacto',
        body: [{ lines: ['InstaMovers Florida LLC', 'Correo electrónico: instamoversfl@gmail.com', 'Teléfono: +1 (656) 268-0800', 'Sitio web: https://floridalocalmovers.instamoversfl.com'] }],
      },
    ],
  },
};
